import { useSelector } from 'react-redux'
import { cn } from '@wellesley/ui'
import * as emoji from 'node-emoji'
import { emojisSlice } from 'components/emojis/emojisSlice'
import measureCaretPosition from 'helpers/measureCaretPosition'
import {
  useGetApiV1SearchQuery,
  useGetApiV1TagsSearchQuery,
  SearchFilter,
} from '@wellesley/openapi'
import { useEffect, useState, ChangeEvent, RefObject, useMemo } from 'react'

import { searchEmoji } from '../helpers/searchEmoji'
import splitTextBySelection from '../helpers/splitTextBySelection'
import {
  mapAutocompleteHashtags,
  mapAutocompleteUsers,
} from '../helpers/mapAutocompleteList'
import { getClearValue } from '../helpers/getClearValue'

export type Field = HTMLTextAreaElement | HTMLInputElement

type Ref = RefObject<Field>

export type UseAutoCompleteProps = {
  text: string
  inputRef: Ref
  setText: (text: string) => void
  onChange(evt: ChangeEvent<Field>): void
  userMentionSearchScope?: {
    groupId?: string
  }
}

const SEARCH_CHARS_LENGTH = 3

const useAutoComplete = ({
  text,
  setText,
  onChange,
  inputRef,
  userMentionSearchScope,
}: UseAutoCompleteProps) => {
  const [search, setSearch] = useState<{
    value: string
    filter: string | null
  }>({
    value: '',
    filter: null,
  })
  const [isOpen, setOpen] = useState(false)

  const isTagsFilter = search.filter === 'tags'
  const hasValidLength = search.value.length >= SEARCH_CHARS_LENGTH + 1
  const canSkipSearch = !search.filter || !hasValidLength || !isOpen

  const searchValue = getClearValue(search.value)

  const { data: searchData } = useGetApiV1SearchQuery(
    {
      q: searchValue,
      filter: search.filter ? ([search.filter] as SearchFilter[]) : undefined,
      ...(search.filter === 'users' && userMentionSearchScope?.groupId
        ? { groupId: userMentionSearchScope.groupId }
        : {}),
    },
    { skip: canSkipSearch || isTagsFilter },
  )

  const { data: tagsSearchData } = useGetApiV1TagsSearchQuery(
    {
      name: searchValue,
      scope: 'global',
    },
    { skip: canSkipSearch || !isTagsFilter },
  )

  const customEmojis = useSelector(emojisSlice.selectors.selectEmojis)
  const searchEmojiMemo = useMemo(
    () =>
      !hasValidLength || !isOpen ? [] : searchEmoji(search.value.slice(1)),
    [canSkipSearch, search.value],
  )
  const allEmojis = useMemo(
    () => [
      ...(customEmojis || []),
      ...(searchEmojiMemo || []).filter(
        (e) => !customEmojis.some((c) => c.shortcode === e.name?.slice(0, -1)),
      ),
    ],
    [customEmojis, searchEmojiMemo],
  )

  const [cursorPosition, setCursorPosition] = useState(0)

  const input = inputRef.current

  const position = measureCaretPosition(
    text,
    cursorPosition,
    cn('leading-5 absolute left-0 top-0 opacity-0 p-0 m-0'),
    `${input?.clientWidth || 544}px`,
  )

  const [savedPosition, setSavedPosition] = useState<typeof position>()

  useEffect(() => {
    if (search.value.length > 1 && !savedPosition && position) {
      const adjustedPosition = {
        ...position,
        top: position.top - (inputRef.current?.scrollTop || 0),
      }
      setSavedPosition(adjustedPosition)
    }
  }, [search.value])

  const handleChangeWithHashtagAutocomplete = (evt: ChangeEvent<Field>) => {
    const text = evt.target.value
    const selectionStart = evt.target.selectionStart || 0

    setCursorPosition(selectionStart)

    const textBeforeSelection = splitTextBySelection(text, selectionStart)

    const textArr = textBeforeSelection.split(/[\s\r\n]+/)

    const tagIndex = textArr.findLastIndex(
      (word) =>
        word.startsWith('#') || word.startsWith('@') || word.startsWith(':'),
    )

    const tag = textArr[tagIndex]

    const filter = tag?.startsWith('@')
      ? 'users'
      : tag?.startsWith('#')
        ? 'tags'
        : null

    setSearch({
      filter,
      value: tag || '',
    })

    if (
      tag?.length < SEARCH_CHARS_LENGTH ||
      textBeforeSelection.lastIndexOf(tag) + tag?.length !== selectionStart
    ) {
      handleCloseAutocomplete()
    } else {
      setOpen(true)
    }

    onChange(evt)
  }

  const handleSelectHashtag = (tag: string) => {
    const startHashIndex = text
      .slice(0, cursorPosition)
      .lastIndexOf(search.value[0])

    const newText =
      text.slice(0, startHashIndex) +
      text.slice(startHashIndex).replace(getClearValue(search.value), tag)

    const emojiText = emoji.emojify(newText, {
      format: (name, part) => {
        if (customEmojis.some((e) => e.shortcode === part?.slice(1, -1)))
          return part as string
        return name
      },
    })

    setText(emojiText)

    handleCloseAutocomplete()
    input?.focus()
  }

  const handleCloseAutocomplete = () => {
    setSavedPosition(undefined)
    setOpen(false)
  }

  const autocompleteList = search.value.startsWith(':')
    ? allEmojis
    : search.value.startsWith('#')
      ? mapAutocompleteHashtags(tagsSearchData || [])
      : mapAutocompleteUsers(searchData?.users || [])

  const autocompleteListFiltered =
    autocompleteList?.filter((item) =>
      item.name?.toLowerCase().includes(search.value.slice(1).toLowerCase()),
    ) || []

  const canShowAutocomplete =
    isOpen && !!autocompleteListFiltered.length && !!position

  return [
    search,
    savedPosition,
    autocompleteListFiltered,
    canShowAutocomplete,
    handleSelectHashtag,
    handleCloseAutocomplete,
    handleChangeWithHashtagAutocomplete,
  ] as const
}

export default useAutoComplete
